import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useHeader } from '@/contexts';
import type { SimplesNacionalDocumento } from '@/services/SimplesNacional/GestaoDocumentos/api/ListaDocumentosSimplesApi';
import type { TipoDocumento } from './GestaoDocumentosTable';
import { MODELOS_COM_ITENS, MODELOS_COM_EVENTOS } from '../GestaoDocumentosProvider';
import { TabItens } from './TabItens';
import { TabEventos } from './TabEventos';
import { formatCNPJ, formatData, formatMoeda, primeiroDiaMes, hoje } from '@/lib/utils/formatters';

interface Props {
  doc: SimplesNacionalDocumento | null;
  tipo: TipoDocumento;
  open: boolean;
  onClose: () => void;
}

export function DocumentoDetalheModal({ doc, tipo, open, onClose }: Props) {
  const { filtros } = useHeader();

  if (!doc) return null;

  const temItens = MODELOS_COM_ITENS.has(doc.CodigoModelo);
  const temEventos = MODELOS_COM_EVENTOS.has(doc.CodigoModelo);
  const cnpjContribuinte = filtros.empresaId ?? '';
  const dataInicio = filtros.dataInicio ?? primeiroDiaMes();
  const dataFim = filtros.dataFim ?? hoje();
  const contraparteLabel = tipo === 'entradas' ? 'Emitente' : 'Destinatário';
  const ibsUF = parseFloat(doc.ValorIBSUF || '0') || 0;
  const ibsMun = parseFloat(doc.ValorIBSMun || '0') || 0;
  const ibsIguais = ibsUF > 0 && ibsMun > 0 && ibsUF === ibsMun;
  const ibsDiferentes = ibsUF > 0 && ibsMun > 0 && ibsUF !== ibsMun;
  // Valor único para exibição: se iguais usa um; se só um tem valor usa esse; se diferentes mostra os dois separados
  const valorIbsSimples = ibsIguais ? ibsMun : ibsMun > 0 ? ibsMun : ibsUF;
  const valorCbs = parseFloat(doc.ValorCBS || '0') || 0;
  const statusLabel =
    doc.Status === '4'
      ? 'Autorizado'
      : doc.Status === '5'
        ? 'Denegado'
        : doc.Status === '7'
          ? 'Cancelado'
          : doc.Status === '8'
            ? 'Divergência'
            : doc.Status || '—';
  const statusVariant =
    statusLabel === 'Autorizado'
      ? 'default'
      : statusLabel === 'Denegado'
        ? 'destructive'
        : 'secondary';

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="flex h-[92vh] w-[96vw]! max-w-[96vw]! flex-col overflow-hidden p-0 lg:max-w-[92vw]! 2xl:max-w-[1700px]!">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex flex-wrap items-center gap-3 pr-10 text-xl font-semibold leading-tight lg:text-2xl">
            <span>DANFE - Documento Auxiliar da Nota Fiscal Eletrônica</span>
            <Badge variant={tipo === 'entradas' ? 'default' : 'destructive'}>
              {tipo === 'entradas' ? 'ENTRADA' : 'SAÍDA'}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-base text-muted-foreground">
            {doc.Modelo} - {doc.Numero}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-6 overflow-y-auto overflow-x-hidden px-6 py-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Valores</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4 xl:grid-cols-7">
              <div>
                <p className="text-muted-foreground">Valor Total NF</p>
                <p className="font-bold text-lg">{formatMoeda(doc.vNF)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Base de Cálculo</p>
                <p className="font-medium">{formatMoeda(doc.BaseCalculo)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">ICMS</p>
                <p className="font-medium">{formatMoeda(doc.ValorICMS)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">PIS</p>
                <p className="font-medium">{formatMoeda(doc.ValorPIS)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">COFINS</p>
                <p className="font-medium">{formatMoeda(doc.ValorCOFINS)}</p>
              </div>
              {ibsDiferentes ? (
                <>
                  <div>
                    <p className="text-muted-foreground">IBS UF</p>
                    <p className="font-medium">{formatMoeda(doc.ValorIBSUF)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">IBS Municipal</p>
                    <p className="font-medium">{formatMoeda(doc.ValorIBSMun)}</p>
                  </div>
                </>
              ) : (
                <div>
                  <p className="text-muted-foreground">IBS</p>
                  <p className="font-medium">{formatMoeda(String(valorIbsSimples))}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground">CBS</p>
                <p className="font-medium">{formatMoeda(doc.ValorCBS)}</p>
              </div>
            </CardContent>
          </Card>

          {(valorIbsSimples > 0 || valorCbs > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Reforma Tributária - IBS e CBS</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2 xl:grid-cols-4">
                {valorIbsSimples > 0 && (
                  <div className="p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
                    <p className="text-muted-foreground mb-2">
                      IBS <span className="text-xs">(0,1%)</span>
                    </p>
                    <p className="font-bold text-lg text-orange-600">
                      {formatMoeda(String(valorIbsSimples))}
                    </p>
                  </div>
                )}
                {valorCbs > 0 && (
                  <div className="p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                    <p className="text-muted-foreground mb-2">
                      CBS <span className="text-xs">(0,9%)</span>
                    </p>
                    <p className="font-bold text-lg text-purple-600">{formatMoeda(doc.ValorCBS)}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Dados do Documento</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-4 gap-4 text-sm">
              <div className="col-span-2">
                <p className="text-muted-foreground">{contraparteLabel}</p>
                <p className="font-medium truncate">{doc.RazaoSocial || '—'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-muted-foreground">CNPJ</p>
                <p className="font-medium">{formatCNPJ(doc.CNPJ) || '—'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-muted-foreground">Chave de Acesso</p>
                <p className="font-mono text-xs break-all">{doc.ChaveAcessoDOC || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Número</p>
                <p className="font-medium">{doc.Numero || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Data de Emissão</p>
                <p className="font-medium">{formatData(doc.Data)}</p>
              </div>
            </CardContent>
          </Card>

          {temItens && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Itens do Documento</CardTitle>
              </CardHeader>
              <CardContent>
                <TabItens
                  chaveAcesso={doc.ChaveAcessoDOC}
                  codigoModelo={doc.CodigoModelo}
                  cnpjContribuinte={cnpjContribuinte}
                  dataInicio={dataInicio}
                  dataFim={dataFim}
                  tipo={tipo}
                />
              </CardContent>
            </Card>
          )}

          {temEventos && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Eventos</CardTitle>
              </CardHeader>
              <CardContent>
                <TabEventos chaveAcesso={doc.ChaveAcessoDOC} codigoModelo={doc.CodigoModelo} />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informações Adicionais</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2 xl:grid-cols-4">
              <div>
                <p className="text-muted-foreground">Status</p>
                <Badge variant={statusVariant}>{statusLabel}</Badge>
              </div>
              <div>
                <p className="text-muted-foreground">Modelo</p>
                <p className="font-medium">{doc.Modelo || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Código Modelo</p>
                <p className="font-medium">{doc.CodigoModelo || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">CFOP</p>
                <p className="font-medium">{doc.CFOP || '—'}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
